import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Gift, Trash2, Copy, RefreshCw, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatApiErrorDetail } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURATIONS = [
  { label: "1 month", days: 30, forever: false },
  { label: "3 months", days: 90, forever: false },
  { label: "1 year", days: 365, forever: false },
  { label: "Forever", days: 0, forever: true },
];

const randomCode = () =>
  "BEAT" + Math.random().toString(36).slice(2, 7).toUpperCase();

export default function AdminPanel({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [compIdx, setCompIdx] = useState(2); // default 1 year
  const [busy, setBusy] = useState(false);

  const [code, setCode] = useState(randomCode());
  const [promoIdx, setPromoIdx] = useState(2);
  const [maxRed, setMaxRed] = useState("");
  const [promos, setPromos] = useState([]);

  const loadPromos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/promo-codes`);
      setPromos(data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (open) loadPromos();
  }, [open, loadPromos]);

  const grant = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    setBusy(true);
    const d = DURATIONS[compIdx];
    try {
      await axios.post(`${API}/admin/grant-pro`, { email: email.trim(), days: d.days, forever: d.forever });
      toast.success(`Pro granted to ${email} (${d.label})`);
      setEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not grant Pro");
    }
    setBusy(false);
  };

  const revoke = async () => {
    if (!email.trim()) return toast.error("Enter an email");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/revoke-pro`, { email: email.trim() });
      toast.success(`Comped Pro revoked for ${email}`);
      setEmail("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not revoke");
    }
    setBusy(false);
  };

  const createPromo = async () => {
    const d = DURATIONS[promoIdx];
    setBusy(true);
    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: code.trim(),
        days: d.days,
        forever: d.forever,
        max_redemptions: maxRed ? Number(maxRed) : null,
      });
      toast.success(`Code ${code.toUpperCase()} created`);
      setCode(randomCode());
      setMaxRed("");
      loadPromos();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create code");
    }
    setBusy(false);
  };

  const deletePromo = async (c) => {
    try {
      await axios.delete(`${API}/admin/promo-codes/${c}`);
      loadPromos();
    } catch (_) {
      toast.error("Could not delete code");
    }
  };

  const copyCode = (c) => {
    navigator.clipboard.writeText(c);
    toast.success(`Copied ${c}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-white max-w-lg" data-testid="admin-panel">
        <DialogHeader>
          <DialogTitle className="font-display tracking-widest flex items-center gap-2 text-[#00e5ff]">
            <Shield size={18} /> ADMIN — GIVE AWAY PRO
          </DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">
            Grant free Pro to any account by email, or create shareable promo codes.
          </DialogDescription>
        </DialogHeader>

        {/* Comp by email */}
        <div className="rounded-md border border-[#262626] p-3">
          <h4 className="hw-label text-[10px] flex items-center gap-1.5 mb-2"><Gift size={12} className="text-[#ff007f]" /> Comp by email</h4>
          <input
            data-testid="admin-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@email.com"
            className="w-full bg-[#050505] border border-[#262626] rounded-sm px-3 py-2 text-sm outline-none focus:border-[#00e5ff]"
          />
          <div className="flex items-center gap-2 mt-2">
            <select
              data-testid="admin-comp-duration"
              value={compIdx}
              onChange={(e) => setCompIdx(Number(e.target.value))}
              className="bg-[#050505] border border-[#262626] rounded-sm px-2 py-2 text-xs font-mono-data text-[#00e5ff] outline-none"
            >
              {DURATIONS.map((d, i) => (
                <option key={d.label} value={i} className="bg-[#141414]">{d.label}</option>
              ))}
            </select>
            <button
              data-testid="admin-grant-btn"
              onClick={grant}
              disabled={busy}
              className="flex-1 py-2 rounded-sm bg-[#00e5ff] text-black text-xs font-bold disabled:opacity-50"
            >
              Grant Pro
            </button>
            <button
              data-testid="admin-revoke-btn"
              onClick={revoke}
              disabled={busy}
              className="py-2 px-3 rounded-sm border border-[#ff3333] text-[#ff3333] text-xs font-bold disabled:opacity-50"
            >
              Revoke
            </button>
          </div>
        </div>

        {/* Promo codes */}
        <div className="rounded-md border border-[#262626] p-3">
          <h4 className="hw-label text-[10px] flex items-center gap-1.5 mb-2"><Sparkles size={12} className="text-[#ffd166]" /> Promo codes</h4>
          <div className="flex items-center gap-2">
            <input
              data-testid="admin-promo-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="flex-1 bg-[#050505] border border-[#262626] rounded-sm px-3 py-2 text-sm font-mono-data text-[#ffd166] outline-none focus:border-[#ffd166]"
            />
            <button onClick={() => setCode(randomCode())} title="Random code" className="p-2 rounded-sm border border-[#262626] text-neutral-400 hover:text-white">
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <select
              data-testid="admin-promo-duration"
              value={promoIdx}
              onChange={(e) => setPromoIdx(Number(e.target.value))}
              className="bg-[#050505] border border-[#262626] rounded-sm px-2 py-2 text-xs font-mono-data text-[#ffd166] outline-none"
            >
              {DURATIONS.map((d, i) => (
                <option key={d.label} value={i} className="bg-[#141414]">{d.label}</option>
              ))}
            </select>
            <input
              data-testid="admin-promo-max"
              value={maxRed}
              onChange={(e) => setMaxRed(e.target.value.replace(/\D/g, ""))}
              placeholder="Max uses (blank = ∞)"
              className="flex-1 bg-[#050505] border border-[#262626] rounded-sm px-2 py-2 text-xs outline-none"
            />
            <button
              data-testid="admin-create-promo-btn"
              onClick={createPromo}
              disabled={busy}
              className="py-2 px-3 rounded-sm bg-[#ffd166] text-black text-xs font-bold disabled:opacity-50"
            >
              Create
            </button>
          </div>

          <div className="mt-3 max-h-40 overflow-y-auto flex flex-col gap-1.5" data-testid="admin-promo-list">
            {promos.length === 0 && <p className="text-neutral-600 text-xs">No codes yet.</p>}
            {promos.map((p) => (
              <div key={p.code} className="flex items-center gap-2 text-xs bg-[#0a0a0a] border border-[#1f1f1f] rounded-sm px-2 py-1.5">
                <span className="font-mono-data text-[#ffd166] font-bold">{p.code}</span>
                <span className="text-neutral-500">{p.forever ? "forever" : `${p.days}d`}</span>
                <span className="text-neutral-600">
                  {p.redeemed_count}{p.max_redemptions ? `/${p.max_redemptions}` : ""} used
                </span>
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => copyCode(p.code)} className="p-1 text-neutral-400 hover:text-white"><Copy size={13} /></button>
                  <button onClick={() => deletePromo(p.code)} className="p-1 text-neutral-500 hover:text-[#ff3333]"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
